import { Fragment, useMemo, useRef, type ReactNode } from 'react';
import { Button, Stack } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import type { FieldErrors, UseFormProps } from 'react-hook-form';
import type { AnyObjectSchema } from 'yup';

import type { LeaseDetailsFormConfig } from './lease-details.config';
import { ApplicantsSection } from './sections/applicants';
import { CoverageSection } from './sections/coverage';
import { LeaseInformationSection } from './sections/lease-information';
import { ScreeningSection } from './sections/screening';
import { buildLeaseDetailsSchema } from './lease-details.schema';
import { collectFieldDefaults } from './lease-details.defaults';
import { LeaseDetailsUIProvider } from './lease-details-ui.context';
import { useVisibilityRule } from './hooks';

// Form engine shared by Renter Invite (drawer) and Deal Lease Details
// (accordion). The engine never branches on consumer identity — all
// per-surface behavior lives in the `LeaseDetailsFormConfig` the caller
// passes in. See `lease-details.config.ts` for the shape.

export type LeaseDetailsFormValues = Record<string, unknown>;

export type LeaseDetailsFormProps = {
  config: LeaseDetailsFormConfig;
  /**
   * Hydration map for an edit flow. If omitted, each field falls back to its
   * `FieldConfig.defaultValue`. The `applicants` array is padded up to
   * `applicantsConfig.min` regardless of what's provided here.
   */
  initialValues?: Partial<LeaseDetailsFormValues>;
  /**
   * Overrides the config-derived schema. Migration aid so a consumer can keep
   * its existing handwritten schema while adopting the engine; dropped once
   * validation is fully config-derived (P4.3).
   */
  schema?: AnyObjectSchema;
  /** RHF validation mode. Defaults to `'onBlur'`. */
  mode?: UseFormProps['mode'];
  /** RHF `shouldUnregister`. Defaults to `false`. */
  shouldUnregister?: UseFormProps['shouldUnregister'];
  /** Fires after `config.submit.submit()` resolves. */
  onSuccess?: (result: unknown, values: LeaseDetailsFormValues) => void;
  /** Fires when `config.submit.submit()` rejects. */
  onError?: (error: unknown, values: LeaseDetailsFormValues) => void;
  /**
   * Fires when a submit is blocked by client validation (RHF's invalid
   * branch). Carries the field errors and current values for analytics.
   */
  onValidationError?: (
    errors: FieldErrors<LeaseDetailsFormValues>,
    values: LeaseDetailsFormValues,
  ) => void;
  /**
   * Fires on cancel-button click. The engine doesn't unmount itself; the
   * consumer dismisses its outer chrome (drawer close, accordion collapse).
   */
  onCancel?: () => void;
};

const APPLICANTS_KEY = 'applicants';
const FLAT_SECTION_KEYS = ['leaseInformation', 'screening', 'coverage'] as const;

// Field names across the three flat sections share one RHF form-state object
// (the engine doesn't namespace by section). Collisions would cause the later
// spread to silently overwrite the earlier — both fields would render but
// share state. Fail fast with a clear message instead.
const assertNoFieldCollisions = (config: LeaseDetailsFormConfig): void => {
  const ownerBySectionFieldName = new Map<string, (typeof FLAT_SECTION_KEYS)[number]>();
  for (const sectionKey of FLAT_SECTION_KEYS) {
    for (const name of Object.keys(config.sections[sectionKey].fields)) {
      if (name === APPLICANTS_KEY) {
        throw new Error(
          `LeaseDetailsForm: field name '${APPLICANTS_KEY}' is reserved for the applicants array (collided in section '${sectionKey}').`,
        );
      }
      const existing = ownerBySectionFieldName.get(name);
      if (existing) {
        throw new Error(
          `LeaseDetailsForm: field name '${name}' is declared in both '${existing}' and '${sectionKey}'. Field names must be unique across leaseInformation, screening, and coverage — they share a single flat form-state object.`,
        );
      }
      ownerBySectionFieldName.set(name, sectionKey);
    }
  }
};

// Applicants is handled separately from scalar fields: the array is padded up
// to `applicantsConfig.min` so the form always mounts at or above the floor,
// regardless of what `initialValues` provides.
const buildInitialFormValues = (
  config: LeaseDetailsFormConfig,
  initialValues?: Partial<LeaseDetailsFormValues>,
): LeaseDetailsFormValues => {
  assertNoFieldCollisions(config);

  // Separate `applicants` so it doesn't get clobbered by the top-level spread —
  // we need to merge it with `min`-floor seeding, not replace it.
  const { applicants: providedApplicants, ...flatInitial } = initialValues ?? {};

  const defaults: LeaseDetailsFormValues = {
    ...collectFieldDefaults(config.sections.leaseInformation.fields),
    ...collectFieldDefaults(config.sections.screening.fields),
    ...collectFieldDefaults(config.sections.coverage.fields),
    ...flatInitial,
  };

  const applicantsConfig = config.sections.applicants;
  const provided = Array.isArray(providedApplicants) ? [...providedApplicants] : [];
  while (provided.length < applicantsConfig.min) {
    provided.push(collectFieldDefaults(applicantsConfig.fields));
  }
  defaults[APPLICANTS_KEY] = provided;

  return defaults;
};

// Wrap in a `LocalizationProvider` (MUI date pickers) and ensure an
// `AnalyticsContext` is available if any `events` callback calls `track()`.
export const LeaseDetailsForm = ({
  config,
  initialValues,
  schema: providedSchema,
  mode = 'onBlur',
  shouldUnregister = false,
  onSuccess,
  onError,
  onValidationError,
  onCancel,
}: LeaseDetailsFormProps) => {
  const schema = useMemo(
    () => providedSchema ?? buildLeaseDetailsSchema(config),
    [providedSchema, config],
  );
  const defaultValues = useMemo(
    () => buildInitialFormValues(config, initialValues),
    [config, initialValues],
  );

  const methods = useForm<LeaseDetailsFormValues>({
    resolver: yupResolver(schema),
    defaultValues,
    mode,
    shouldUnregister,
  });

  // Incremented on every submit attempt — valid and invalid — so consumers can
  // report a stable attempt number (e.g. invite's `submit_attempt_number`).
  const submitAttemptRef = useRef(0);

  const uiContextValue = useMemo(
    () => ({ inputSize: config.ui?.inputSize ?? ('medium' as const) }),
    [config.ui?.inputSize],
  );

  const handleSubmit = methods.handleSubmit(
    async (values) => {
      submitAttemptRef.current += 1;
      try {
        const payload = config.submit.buildPayload(values);
        const result = await config.submit.submit(payload, {
          values,
          setError: methods.setError,
          attemptNumber: submitAttemptRef.current,
        });
        onSuccess?.(result, values);
      } catch (error) {
        methods.setError('root', { type: 'submit', message: 'Submission failed' });
        onError?.(error, values);
      }
    },
    (errors) => {
      submitAttemptRef.current += 1;
      onValidationError?.(errors, methods.getValues());
    },
  );

  return (
    <FormProvider {...methods}>
      <LeaseDetailsUIProvider value={uiContextValue}>
        <form onSubmit={handleSubmit} noValidate id="lease-details-form">
          <Stack direction="column" spacing={config.ui?.contentSpacing ?? 3}>
            {config.ui?.slots?.top}
            <SectionsRenderer config={config} />
            {!config.ui?.hideDefaultActions && (
              <FormActions
                submitLabel={config.ui?.submitButtonLabel ?? 'Submit'}
                cancelLabel={config.ui?.cancelButtonLabel ?? 'Cancel'}
                onCancel={onCancel}
              />
            )}
          </Stack>
          {/* Rendered outside the content Stack — a direct `<form>` child — so a
              sticky footer's full-bleed negative margins extend to the drawer
              edges (flex `align-items: stretch` swallows them). */}
          {config.ui?.slots?.bottom}
        </form>
      </LeaseDetailsUIProvider>
    </FormProvider>
  );
};

type SectionsRendererProps = {
  config: LeaseDetailsFormConfig;
};

// Visibility resolution lives in a child component so the `useVisibilityRule`
// hooks (which `useWatch` under the hood) run inside the surrounding
// `<FormProvider>`. Hoisting them into `LeaseDetailsForm`'s body would call
// `useFormContext()` before the provider mounts and crash with a null context.
const SectionsRenderer = ({ config }: SectionsRendererProps) => {
  const leaseInformationVisible = useVisibilityRule(config.sections.leaseInformation.visible);
  const applicantsVisible = useVisibilityRule(config.sections.applicants.visible);
  const screeningVisible = useVisibilityRule(config.sections.screening.visible);
  const coverageVisible = useVisibilityRule(config.sections.coverage.visible);

  const visibleSections: Array<{ key: string; node: ReactNode }> = [
    leaseInformationVisible && {
      key: 'leaseInformation',
      node: <LeaseInformationSection config={config.sections.leaseInformation} />,
    },
    applicantsVisible && {
      key: 'applicants',
      node: <ApplicantsSection config={config.sections.applicants} />,
    },
    screeningVisible && {
      key: 'screening',
      node: <ScreeningSection config={config.sections.screening} />,
    },
    coverageVisible && {
      key: 'coverage',
      node: <CoverageSection config={config.sections.coverage} />,
    },
  ].filter(Boolean) as Array<{ key: string; node: ReactNode }>;

  const sectionDivider = config.ui?.sectionDivider;

  return (
    <>
      {visibleSections.map((section, index) => (
        <Fragment key={section.key}>
          {index > 0 && sectionDivider}
          {section.node}
        </Fragment>
      ))}
    </>
  );
};

type FormActionsProps = {
  submitLabel: string;
  cancelLabel: string;
  onCancel?: () => void;
};

const FormActions = ({ submitLabel, cancelLabel, onCancel }: FormActionsProps) => {
  const { formState } = useFormContext();
  return (
    <Stack direction={{ sm: 'row', xs: 'column-reverse' }} spacing={2} justifyContent="flex-end">
      {onCancel && (
        <Button
          id="lease-details-cancel-button"
          onClick={onCancel}
          variant="outlined"
          color="primary"
        >
          {cancelLabel}
        </Button>
      )}
      <Button
        id="lease-details-submit-button"
        type="submit"
        variant="contained"
        color="primary"
        disabled={formState.isSubmitting}
      >
        {submitLabel}
      </Button>
    </Stack>
  );
};
